import React from "react";
import { motion } from "framer-motion";
import { Heart, Sparkles, Brain, Puzzle, Sunrise, Users } from "lucide-react";

const areas = [
  {
    icon: Heart,
    title: "Elderly Care",
    tag: "01",
    desc: "Fall prevention, behavioural insights and gentle companions that protect independence at home.",
    image:
      "https://images.pexels.com/photos/18509794/pexels-photo-18509794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: Sunrise,
    title: "Longevity & Healthy Aging",
    tag: "02",
    desc: "AI that translates daily signals into lifespan and healthspan insights for better, longer lives.",
    image: "/longevity-aging.jpg",
  },
  {
    icon: Puzzle,
    title: "Autism Spectrum Support",
    tag: "03",
    desc: "Sensory‑aware tools and personalised routines that support individuals on the spectrum and their families.",
    image: "/autism-support.jpg",
  },
  {
    icon: Brain,
    title: "Cognitive Health",
    tag: "04",
    desc: "Early signal detection for memory and cognition, surfacing patterns long before traditional care can.",
    image:
      "https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwc21pbGluZyUyMGNhcmVnaXZlcnxlbnwwfHx8fDE3NzkzNzM3MzB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Sparkles,
    title: "Mental Wellness",
    tag: "05",
    desc: "Compassionate conversational AI for mood, mindfulness and emotional support — always available, never invasive.",
    image: "/mental-wellness.jpg",
  },
  {
    icon: Users,
    title: "Caregiver Support",
    tag: "06",
    desc: "Tools that reduce burnout, balance workload and bring families and professionals into the same loop.",
    image: "/caregiver-support.jpg",
  },
];

export const FocusAreas = () => {
  return (
    <section
      id="focus"
      className="relative py-24 md:py-32 bg-[#F2EBE5]"
      data-testid="focus-areas-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-16"
        >
          <div className="text-xs tracking-[0.24em] uppercase text-sentivo-secondary mb-6">
            Focus Areas
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-sentivo-ink text-balance">
            Six spaces where AI can
            <span className="italic text-sentivo-accent"> be more humane.</span>
          </h2>
          <p className="mt-6 text-lg text-sentivo-muted leading-relaxed">
            We choose problems with deep human stakes — where a little intelligence,
            placed gently, can change everything.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {areas.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.article
                key={a.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                whileHover={{ y: -4 }}
                className={`group relative overflow-hidden rounded-3xl bg-white border border-sentivo-line p-2 md:p-2 ${
                  i % 3 === 0 ? "md:translate-y-0" : "md:translate-y-6"
                }`}
                data-testid={`focus-area-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden rounded-2xl">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-sentivo-primary">
                    <span>{a.tag}</span>
                    <span className="opacity-40">/</span>
                    <span>Focus</span>
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-sentivo-surface text-sentivo-accent">
                      <Icon size={16} strokeWidth={1.8} />
                    </span>
                    <h3 className="font-serif text-2xl md:text-3xl text-sentivo-ink">
                      {a.title}
                    </h3>
                  </div>
                  <p className="text-sentivo-muted leading-relaxed">{a.desc}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
